import moment from "moment";

export const MAKS_GRAFIK_LAB = 8;

export const PALET_SERI_LAB = [
  "#2a78d6",
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#008300",
  "#6250d6",
  "#e34948"
];

export interface LabItemTren {
  test_id?: string;
  nama_test: string;
  hasil: string;
  satuan: string;
  nilai_normal: string;
  flag: string;
}

export interface LabSampelTren {
  lis_sampel: string;
  tgl_order: string;
  acc_date: string;
  unit_nama?: string | null;
  items: LabItemTren[];
}

export interface OpsiPemeriksaan {
  text: string;
  value: string;
}

export interface Rujukan {
  low: number | null;
  high: number | null;
}

export interface TitikTren {
  waktu: string;
  nilai: number | null;
  hasilTeks: string;
  flag: string;
  rujukanTeks: string;
  rujukan: Rujukan | null;
  satuan: string;
  lisSampel: string;
  unit: string;
}

export interface SeriTren {
  kunci: string;
  nama: string;
  satuan: string;
  titik: TitikTren[];
}

export interface StatusTitik {
  teks: string;
  warna: string;
  ikon: string;
}

const ANGKA = "-?\\d+(?:[.,]\\d+)?";

function rapikan(teks: string): string {
  return (teks || "").replace(/\s+/g, " ").trim();
}

export function kunciPemeriksaan(nama: string): string {
  return rapikan(nama).toUpperCase();
}

export function opsiPemeriksaan(sampel: LabSampelTren[]): OpsiPemeriksaan[] {
  const peta: { [kunci: string]: string } = {};
  (sampel || []).forEach(s => {
    (s.items || []).forEach(i => {
      const kunci = kunciPemeriksaan(i.nama_test);
      if (kunci && !peta[kunci]) peta[kunci] = rapikan(i.nama_test);
    });
  });
  return Object.keys(peta)
    .map(kunci => ({ text: peta[kunci], value: kunci }))
    .sort((a, b) => a.text.localeCompare(b.text, "id"));
}

export function gabungOpsi(
  ...daftar: OpsiPemeriksaan[][]
): OpsiPemeriksaan[] {
  const peta: { [kunci: string]: OpsiPemeriksaan } = {};
  daftar.forEach(opsi => {
    (opsi || []).forEach(o => {
      if (o && o.value && !peta[o.value]) peta[o.value] = o;
    });
  });
  return Object.keys(peta)
    .map(k => peta[k])
    .sort((a, b) => a.text.localeCompare(b.text, "id"));
}

export function parseAngka(teks: string): number | null {
  const t = (teks || "").replace(/\s+/g, "");
  if (!t) return null;
  if (/^-?\d{1,3}(\.\d{3}){2,}(,\d+)?$/.test(t)) {
    return Number(t.replace(/\./g, "").replace(",", "."));
  }
  if (/^-?\d{1,3}(\.\d{3})+,\d+$/.test(t)) {
    return Number(t.replace(/\./g, "").replace(",", "."));
  }
  if (new RegExp("^" + ANGKA + "$").test(t)) {
    return Number(t.replace(",", "."));
  }
  return null;
}

export function parseRujukan(teks: string): Rujukan | null {
  const t = rapikan(teks)
    .replace(/≤/g, "<=")
    .replace(/≥/g, ">=")
    .replace(/[–—]/g, "-");
  if (!t) return null;
  const sisa = "\\s*[^\\d<>=-]*$";

  const rentang = new RegExp(
    "^(" + ANGKA + ")\\s*(?:-|s\\/d|sd|sampai)\\s*(" + ANGKA + ")" + sisa,
    "i"
  ).exec(t);
  if (rentang) {
    const a = parseAngka(rentang[1]);
    const b = parseAngka(rentang[2]);
    if (a === null || b === null) return null;
    return { low: Math.min(a, b), high: Math.max(a, b) };
  }

  const atas = new RegExp("^<=?\\s*(" + ANGKA + ")" + sisa).exec(t);
  if (atas) {
    const v = parseAngka(atas[1]);
    return v === null ? null : { low: null, high: v };
  }

  const bawah = new RegExp("^>=?\\s*(" + ANGKA + ")" + sisa).exec(t);
  if (bawah) {
    const v = parseAngka(bawah[1]);
    return v === null ? null : { low: v, high: null };
  }

  return null;
}

export function bangunSeri(
  sampel: LabSampelTren[],
  kunciList: string[]
): SeriTren[] {
  return (kunciList || []).map(kunci => {
    const titik: TitikTren[] = [];
    let nama = "";
    let satuan = "";
    (sampel || []).forEach(s => {
      (s.items || []).forEach(i => {
        if (kunciPemeriksaan(i.nama_test) !== kunci) return;
        if (!nama) nama = rapikan(i.nama_test);
        const rujukanTeks = rapikan(i.nilai_normal);
        titik.push({
          waktu: s.acc_date || s.tgl_order || "",
          nilai: parseAngka(i.hasil),
          hasilTeks: rapikan(i.hasil),
          flag: rapikan(i.flag).toUpperCase(),
          rujukanTeks,
          rujukan: parseRujukan(rujukanTeks),
          satuan: rapikan(i.satuan),
          lisSampel: s.lis_sampel,
          unit: rapikan(s.unit_nama || "")
        });
      });
    });
    titik.sort((a, b) => (a.waktu < b.waktu ? -1 : a.waktu > b.waktu ? 1 : 0));
    titik.forEach(t => {
      if (t.satuan) satuan = t.satuan;
    });
    return { kunci, nama: nama || kunci, satuan, titik };
  });
}

export function titikNumerik(seri: SeriTren): TitikTren[] {
  return seri.titik.filter(t => t.nilai !== null);
}

export function titikTerakhir(seri: SeriTren): TitikTren | null {
  return seri.titik.length ? seri.titik[seri.titik.length - 1] : null;
}

export function isFlagAbnormal(flag: string): boolean {
  return ["H", "L", "C", "HH", "LL"].indexOf((flag || "").toUpperCase()) >= 0;
}

export function statusTitik(titik: TitikTren | null): StatusTitik | null {
  if (!titik) return null;
  const flag = (titik.flag || "").toUpperCase();
  if (flag === "C" || flag === "HH" || flag === "LL") {
    return { teks: "Kritis", warna: "red darken-2", ikon: "mdi-alert-octagon" };
  }
  if (flag === "H") {
    return {
      teks: "Tinggi",
      warna: "deep-orange darken-2",
      ikon: "mdi-arrow-up-bold"
    };
  }
  if (flag === "L") {
    return {
      teks: "Rendah",
      warna: "deep-orange darken-2",
      ikon: "mdi-arrow-down-bold"
    };
  }
  const r = titik.rujukan;
  if (titik.nilai !== null && r) {
    const diAtasBawah = r.low === null || titik.nilai >= r.low;
    const diBawahAtas = r.high === null || titik.nilai <= r.high;
    if (diAtasBawah && diBawahAtas) {
      return { teks: "Normal", warna: "green darken-2", ikon: "mdi-check-bold" };
    }
  }
  return null;
}

export function formatAngka(nilai: number): string {
  return nilai.toLocaleString("id-ID", { maximumFractionDigits: 2 });
}

export function formatWaktu(waktu: string, pola = "DD-MM-YYYY HH:mm"): string {
  if (!waktu) return "-";
  const m = moment(waktu);
  return m.isValid() ? m.format(pola) : waktu;
}

export function teksTren(seri: SeriTren): string {
  const numerik = titikNumerik(seri);
  const bagian: string[] = [];
  if (numerik.length === 1) {
    bagian.push("Hanya 1 hasil numerik dalam periode ini");
  } else if (numerik.length > 1) {
    const awal = numerik[0];
    const akhir = numerik[numerik.length - 1];
    const selisih = (akhir.nilai as number) - (awal.nilai as number);
    const satuan = seri.satuan ? " " + seri.satuan : "";
    const sejak = `dibanding hasil pertama periode (${formatWaktu(awal.waktu)})`;
    if (Math.abs(selisih) < 1e-9) {
      bagian.push(`Tetap ${sejak}`);
    } else {
      bagian.push(
        `${selisih > 0 ? "Naik" : "Turun"} ${formatAngka(
          Math.abs(selisih)
        )}${satuan} ${sejak}`
      );
    }
  }
  const abnormal = seri.titik.filter(t => isFlagAbnormal(t.flag)).length;
  if (seri.titik.length) {
    bagian.push(
      `${abnormal} dari ${seri.titik.length} hasil bertanda abnormal (H/L/C)`
    );
  }
  const nonNumerik = seri.titik.length - numerik.length;
  if (nonNumerik > 0) {
    bagian.push(`${nonNumerik} hasil non-numerik tidak digambar`);
  }
  return bagian.join(" · ");
}

export function aturSlotWarna(
  slotLama: { [kunci: string]: number },
  dipakai: string[]
): { [kunci: string]: number } {
  const baru: { [kunci: string]: number } = {};
  const terpakai: boolean[] = [];
  dipakai.forEach(k => {
    const s = slotLama[k];
    if (s !== undefined && !terpakai[s] && baru[k] === undefined) {
      baru[k] = s;
      terpakai[s] = true;
    }
  });
  dipakai.forEach(k => {
    if (baru[k] !== undefined) return;
    let s = 0;
    while (s < PALET_SERI_LAB.length && terpakai[s]) s++;
    if (s >= PALET_SERI_LAB.length) s = 0;
    baru[k] = s;
    terpakai[s] = true;
  });
  return baru;
}
